/**
 * Cache Manager Utility
 * Handles cache invalidation and service worker updates
 * Works with Vite PWA plugin
 */

export const clearAllCaches = async () => {
  if ("caches" in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    console.log("All caches cleared");
  }
};

export const clearServiceWorkerCache = () => {
  // For Vite PWA, we need to use the registration
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        if (registration.active) {
          registration.active.postMessage({
            type: "CLEAR_CACHE",
          });
          console.log("Service worker cache clear message sent");
        }
      });
    });
  }
};

export const forceReload = () => {
  // Clear all caches
  clearAllCaches();
  clearServiceWorkerCache();

  // Force reload
  window.location.reload(true);
};

export const checkForUpdates = async () => {
  try {
    const response = await fetch("/build-meta.json", {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.version;
  } catch (error) {
    console.error("Failed to check for updates:", error);
    return null;
  }
};

export const isUpdateAvailable = async () => {
  const latestVersion = await checkForUpdates();
  if (!latestVersion) return false;

  const currentVersion = localStorage.getItem("build-version");

  // Normalize both versions to strings for comparison
  const normalizedLatest = String(latestVersion);
  const normalizedCurrent = String(currentVersion);

  return currentVersion && normalizedLatest !== normalizedCurrent;
};

export const updateAppVersion = (version) => {
  localStorage.setItem("build-version", version);
  localStorage.removeItem("update-notification-shown");
};

export const getCurrentVersion = () => {
  return localStorage.getItem("build-version");
};

// Vite PWA specific functions
export const checkPWAUpdate = () => {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        if (registration.waiting) {
          console.log("🔄 PWA update available");
          // Trigger the update
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      });
    });
  }
};

export const registerPWAUpdateHandler = () => {
  if ("serviceWorker" in navigator) {
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", async () => {
      if (refreshing) return; // prevent duplicate reloads
      refreshing = true;

      const latestVersion = await checkForUpdates();
      const currentVersion = getCurrentVersion();

      if (latestVersion && String(latestVersion) !== String(currentVersion)) {
        console.log("New PWA version available, reloading...");
        updateAppVersion(latestVersion);
        window.location.reload();
      } else {
        console.log("No version change, skipping reload");
        refreshing = false; // allow future reloads if really needed
      }
    });
  }
};
